import { useQuery, useMutation, useQueryClient } from 'react-query';
import { AxiosError } from 'axios';
import { Axios } from '@utils/api/axios-instance';
import { URL_EXTENSION } from '@utils/constants/app-constants';
import { BaseError } from '@utils/types/shared/base';
import {
  SystemSettingsResponse,
  SystemSettingDetailResponse,
  CreateSystemSettingPayload,
  UpdateSystemSettingPayload,
  SystemSettingCategory
} from '@utils/types/back-office/settings';

export const SYSTEM_SETTINGS_QUERY_KEY = 'system-settings';

const SYSTEM_SETTINGS_ENDPOINT = `${URL_EXTENSION}/admin/settings/system-settings`;

/**
 * Hook to fetch system settings
 *
 * GET /api/admin/settings/system-settings
 *
 * Query params:
 *   - limit (default: 10)
 *   - offset (default: 0)
 *   - category (optional): 'feature_flag' | 'default' | 'system'
 */
export const useGetSystemSettings = (options?: {
  limit?: number;
  offset?: number;
  category?: SystemSettingCategory;
}) => {
  const { limit = 100, offset = 0, category } = options || {};

  const fetchSystemSettings = async (): Promise<SystemSettingsResponse> => {
    const params = new URLSearchParams({
      limit: String(limit),
      offset: String(offset)
    });

    if (category) {
      params.append('category', category);
    }

    const response = await Axios.get<SystemSettingsResponse>(
      `${SYSTEM_SETTINGS_ENDPOINT}?${params.toString()}`
    );
    return response.data;
  };

  return useQuery<SystemSettingsResponse, AxiosError<BaseError>>(
    [SYSTEM_SETTINGS_QUERY_KEY, limit, offset, category],
    fetchSystemSettings,
    {
      staleTime: 60000,
      refetchOnWindowFocus: false,
      keepPreviousData: true
    }
  );
};

/**
 * Hook to fetch a single system setting by ID
 *
 * GET /api/admin/settings/system-settings/{setting_id}
 */
export const useGetSystemSettingById = (settingId: number | undefined) => {
  const fetchSystemSetting = async (): Promise<SystemSettingDetailResponse> => {
    if (!settingId) {
      throw new Error('Setting ID is required');
    }
    const response = await Axios.get<SystemSettingDetailResponse>(
      `${SYSTEM_SETTINGS_ENDPOINT}/${settingId}`
    );
    return response.data;
  };

  return useQuery<SystemSettingDetailResponse, AxiosError<BaseError>>(
    [SYSTEM_SETTINGS_QUERY_KEY, settingId],
    fetchSystemSetting,
    {
      enabled: !!settingId,
      staleTime: 60000,
      refetchOnWindowFocus: false
    }
  );
};

/**
 * Hook to create a system setting
 *
 * POST /api/admin/settings/system-settings
 *
 * Returns 201 with created setting
 */
export const useCreateSystemSetting = () => {
  const queryClient = useQueryClient();

  return useMutation<
    SystemSettingDetailResponse,
    AxiosError<BaseError>,
    CreateSystemSettingPayload
  >(
    async payload => {
      const response = await Axios.post<SystemSettingDetailResponse>(
        SYSTEM_SETTINGS_ENDPOINT,
        payload
      );
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([SYSTEM_SETTINGS_QUERY_KEY]);
      }
    }
  );
};

/**
 * Hook to update a system setting
 *
 * PATCH /api/admin/settings/system-settings/{setting_id}
 */
export const useUpdateSystemSetting = () => {
  const queryClient = useQueryClient();

  return useMutation<
    SystemSettingDetailResponse,
    AxiosError<BaseError>,
    { id: number; payload: UpdateSystemSettingPayload }
  >(
    async ({ id, payload }) => {
      const response = await Axios.patch<SystemSettingDetailResponse>(
        `${SYSTEM_SETTINGS_ENDPOINT}/${id}`,
        payload
      );
      return response.data;
    },
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries([SYSTEM_SETTINGS_QUERY_KEY]);
        queryClient.invalidateQueries([
          SYSTEM_SETTINGS_QUERY_KEY,
          variables.id
        ]);
      }
    }
  );
};

/**
 * Hook to delete a system setting
 *
 * DELETE /api/admin/settings/system-settings/{setting_id}
 *
 * Returns 204 with no body
 */
export const useDeleteSystemSetting = () => {
  const queryClient = useQueryClient();

  return useMutation<void, AxiosError<BaseError>, number>(
    async settingId => {
      await Axios.delete(`${SYSTEM_SETTINGS_ENDPOINT}/${settingId}`);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([SYSTEM_SETTINGS_QUERY_KEY]);
      }
    }
  );
};

// Helper hook to get settings by category (convenience wrapper)
export const useGetFeatureFlags = () => {
  return useGetSystemSettings({ category: 'feature_flag', limit: 100 });
};

export const useGetDefaults = () => {
  return useGetSystemSettings({ category: 'default', limit: 100 });
};

export const useGetSystemCategorySettings = () => {
  return useGetSystemSettings({ category: 'system', limit: 100 });
};
